import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Plus, Edit2, Trash2, MessageSquare, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import AppHeader from '../components/AppHeader';
import Breadcrumb from '../components/Breadcrumb';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle,
  Card, Button, Input, Label, FormGroup, Select,
  Grid, Flex, Badge, EmptyState, EmptyText, EmptyStateAction,
  LoadingContainer, Spinner, ModalOverlay, ModalBox, ModalHeader, ModalTitle, ModalBody, ModalFooter,
} from '../components/ui';
import { theme } from '../styles/theme';
import api from '../services/api';

const CATEGORIES = ['geral', 'agendamento', 'financeiro', 'triagem', 'encerramento', 'outro'];

const ResponseTemplatesPage = () => {
  const { t } = useTranslation('templates');
  const toast   = useToast();
  const confirm = useConfirm();
  const { tenantId } = useParams();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState({ title: '', text: '', category: 'geral' });
  const [saving, setSaving]       = useState(false);
  const [filter, setFilter]       = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const homeItem = user.role === 'sallus_admin'
    ? { label: 'Libben Agents', path: '/sallus/dashboard' }
    : user.role === 'manager'
      ? { label: t('home', { ns: 'common' }), path: `/manager/${tenantId}` }
      : { label: t('home', { ns: 'common' }), path: `/dashboard/${tenantId}` };

  const breadcrumbItems = [homeItem, { label: t('title') }];

  useEffect(() => { load(); }, [tenantId]); // eslint-disable-line

  const load = async () => {
    try {
      const res = await api.get(`/templates/${tenantId}`);
      setTemplates(res.data.templates || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openNew = () => { setForm({ title: '', text: '', category: 'geral' }); setEditing(null); setShowForm(true); };
  const openEdit = (tpl) => { setForm({ title: tpl.title, text: tpl.text, category: tpl.category || 'geral' }); setEditing(tpl.templateId); setShowForm(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/templates/${tenantId}/${editing}`, form);
      else         await api.post(`/templates/${tenantId}`, form);
      setShowForm(false); setEditing(null); load();
    } catch { toast.error(t('saveError')); }
    finally { setSaving(false); }
  };

  const remove = async (templateId) => {
    const ok = await confirm({ title: t('deleteConfirm'), danger: true });
    if (!ok) return;
    try { await api.delete(`/templates/${tenantId}/${templateId}`); load(); toast.success(t('deleteSuccess', { ns: 'common' })); }
    catch { toast.error(t('deleteError')); }
  };

  const filtered = templates.filter(tpl =>
    !filter || tpl.title.toLowerCase().includes(filter.toLowerCase()) ||
    tpl.text.toLowerCase().includes(filter.toLowerCase()) || tpl.category === filter
  );

  const byCategory = CATEGORIES.reduce((acc, cat) => {
    const items = filtered.filter(tpl => (tpl.category || 'geral') === cat);
    if (items.length > 0) acc[cat] = items;
    return acc;
  }, {});

  if (loading) return (
    <>
      <AppHeader title={t('title')} />
      <PageContainer><LoadingContainer><Spinner /><span>{t('loading', { ns: 'common' })}</span></LoadingContainer></PageContainer>
    </>
  );

  return (
    <>
      <AppHeader title={t('title')} />
      <PageContainer>
        <Breadcrumb items={breadcrumbItems} />
        <PageHeader>
          <div>
            <PageTitle>{t('title')}</PageTitle>
            <PageSubtitle>{t('subtitle')}</PageSubtitle>
          </div>
          <Button variant="primary" onClick={openNew}>
            <Plus size={16} /> {t('newTemplate')}
          </Button>
        </PageHeader>

        {showForm && (
          <ModalOverlay onClick={() => setShowForm(false)}>
            <ModalBox onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{editing ? t('editTemplate') : t('newTemplate')}</ModalTitle>
                <CloseBtn onClick={() => setShowForm(false)}><X size={18} /></CloseBtn>
              </ModalHeader>
              <ModalBody>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[4] }}>
                  <FormGroup>
                    <Label>{t('titleField')}</Label>
                    <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ex: Confirmar dados cadastrais" required />
                    <Helper>{t('titleHint')}</Helper>
                  </FormGroup>
                  <FormGroup>
                    <Label>{t('textField')}</Label>
                    <Textarea rows={4} value={form.text} onChange={e => setForm({ ...form, text: e.target.value })} placeholder="Ex: Olá! Posso confirmar seus dados..." required />
                    <Helper>{t('textHint')}</Helper>
                  </FormGroup>
                  <FormGroup>
                    <Label>{t('category')}</Label>
                    <Select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                      {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                    </Select>
                  </FormGroup>
                  <Flex gap={theme.spacing[3]} justify="flex-end">
                    <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>{t('cancel', { ns: 'common' })}</Button>
                    <Button type="submit" variant="primary" disabled={saving}>
                      {saving ? t('saving') : editing ? t('update') : t('create')}
                    </Button>
                  </Flex>
                </form>
              </ModalBody>
            </ModalBox>
          </ModalOverlay>
        )}

        {templates.length > 0 && (
          <FilterBar>
            <Input value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('searchPlaceholder')} style={{ maxWidth: 300 }} />
            <FilterTags>
              <FilterTag $active={!filter} onClick={() => setFilter('')}>{t('allCount', { count: templates.length })}</FilterTag>
              {CATEGORIES.map(cat => {
                const count = templates.filter(tpl => (tpl.category || 'geral') === cat).length;
                if (!count) return null;
                return <FilterTag key={cat} $active={filter === cat} onClick={() => setFilter(filter === cat ? '' : cat)}>{cat} ({count})</FilterTag>;
              })}
            </FilterTags>
          </FilterBar>
        )}

        {templates.length === 0 ? (
          <EmptyStateAction
            icon={<MessageSquare size={48} strokeWidth={1} />}
            title={t('noTemplates')}
            hint={t('noTemplatesHint')}
            action={openNew}
            actionLabel={t('createFirst')}
          />
        ) : filtered.length === 0 ? (
          <EmptyState><EmptyText>{t('noResults', { filter })}</EmptyText></EmptyState>
        ) : (
          Object.entries(byCategory).map(([cat, items]) => (
            <CategorySection key={cat}>
              <CategoryTitle>
                {t(`categories.${cat}`, { defaultValue: cat.charAt(0).toUpperCase() + cat.slice(1) })}
                <Badge variant="neutral" style={{ marginLeft: theme.spacing[2] }}>{items.length}</Badge>
              </CategoryTitle>
              <Grid min="280px">
                {items.map(tpl => (
                  <TemplateCard key={tpl.templateId}>
                    <TemplateTitle>{tpl.title}</TemplateTitle>
                    <TemplateText>{tpl.text}</TemplateText>
                    <Flex gap={theme.spacing[2]} style={{ marginTop: theme.spacing[3] }}>
                      <Button variant="secondary" size="sm" style={{ flex: 1 }} onClick={() => openEdit(tpl)}>
                        <Edit2 size={12} /> {t('edit', { ns: 'common' })}
                      </Button>
                      <Button variant="danger" size="sm" style={{ flex: 1 }} onClick={() => remove(tpl.templateId)}>
                        <Trash2 size={12} /> {t('delete', { ns: 'common' })}
                      </Button>
                    </Flex>
                  </TemplateCard>
                ))}
              </Grid>
            </CategorySection>
          ))
        )}
      </PageContainer>
    </>
  );
};

const FilterBar = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[3]}; margin-bottom: ${theme.spacing[6]};`;
const FilterTags = styled.div`display: flex; flex-wrap: wrap; gap: ${theme.spacing[2]};`;
const FilterTag = styled.button`padding: ${theme.spacing[1]} ${theme.spacing[3]}; border-radius: ${theme.radii.full}; font-size: ${theme.typography.xs}; font-weight: ${theme.typography.medium}; cursor: pointer; border: 1px solid ${theme.colors.border}; background: ${p => p.$active ? theme.colors.primary : theme.colors.white}; color: ${p => p.$active ? 'white' : theme.colors.textSecondary}; transition: ${theme.transitions.fast}; &:hover { border-color: ${theme.colors.primary}; }`;
const CategorySection = styled.div`margin-bottom: ${theme.spacing[6]};`;
const CategoryTitle = styled.h3`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textSecondary}; text-transform: uppercase; letter-spacing: 0.05em; margin: 0 0 ${theme.spacing[3]} 0; display: flex; align-items: center;`;
const TemplateCard = styled(Card)`display: flex; flex-direction: column; &:hover { border-color: ${theme.colors.primary}; }`;
const TemplateTitle = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary}; margin-bottom: ${theme.spacing[2]};`;
const TemplateText = styled.div`font-size: ${theme.typography.sm}; color: ${theme.colors.textSecondary}; line-height: 1.5; flex: 1; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;`;
const CloseBtn = styled.button`background: none; border: none; cursor: pointer; color: ${theme.colors.textMuted}; display: flex; align-items: center; &:hover { color: ${theme.colors.textPrimary}; }`;
const Textarea = styled.textarea`width: 100%; padding: ${theme.spacing[2]} ${theme.spacing[3]}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md}; font-size: ${theme.typography.sm}; font-family: inherit; resize: vertical; box-sizing: border-box; outline: none; line-height: 1.5; &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }`;
const Helper = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: ${theme.spacing[1]};`;

export default ResponseTemplatesPage;
